export type StructuredCustomerAddress = {
  village?: string | null;
  district?: string | null;
  province?: string | null;
  addressLine?: string | null;
};

export type ParsedCustomerAddress = {
  village: string | null;
  district: string | null;
  province: string | null;
  addressLine: string | null;
  /** true เมื่อแยกได้อย่างน้อย 1 ฟิลด์ที่มีความหมาย */
  parsed: boolean;
};

export type ResolvedCustomerAddressWrite = {
  address: string | null;
  /** true = เขียนฟิลด์แยกด้วย (create หรือ client ส่ง structured มา) */
  touchStructured: boolean;
  village: string | null;
  district: string | null;
  province: string | null;
  addressLine: string | null;
};

function cleanPart(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * ประกอบข้อความที่อยู่สำหรับแสดง/พิมพ์
 * รูปแบบ: [addressLine, ]ບ village, ມ district, ຂ province — ข้ามส่วนที่ว่าง
 */
export function formatCustomerAddress(
  parts: StructuredCustomerAddress,
): string | null {
  const village = cleanPart(parts.village);
  const district = cleanPart(parts.district);
  const province = cleanPart(parts.province);
  const addressLine = cleanPart(parts.addressLine);

  const chunks: string[] = [];
  if (addressLine) chunks.push(addressLine);
  if (village) chunks.push(`ບ ${village}`);
  if (district) chunks.push(`ມ ${district}`);
  if (province) chunks.push(`ຂ ${province}`);

  if (chunks.length === 0) return null;
  return chunks.join(", ");
}

/**
 * แสดงที่อยู่ — ใช้ฟิลด์แยกก่อน แล้วค่อย fallback ไป address เดิม
 */
export function displayCustomerAddress(
  parts: StructuredCustomerAddress & { address?: string | null },
): string | null {
  return formatCustomerAddress(parts) ?? cleanPart(parts.address);
}

export type CustomerAddressFormValue = {
  province: string;
  district: string;
  village: string;
  addressLine: string;
};

export const EMPTY_CUSTOMER_ADDRESS_FORM: CustomerAddressFormValue = {
  province: "",
  district: "",
  village: "",
  addressLine: "",
};

/** ยังไม่มีฟิลด์แยกใน DB — ต้องแยกมือ / แสดง hint */
export function needsManualAddressSplit(c: {
  village?: string | null;
  district?: string | null;
  province?: string | null;
  addressLine?: string | null;
  address?: string | null;
}): boolean {
  const hasStructured = Boolean(
    cleanPart(c.village) ||
      cleanPart(c.district) ||
      cleanPart(c.province) ||
      cleanPart(c.addressLine),
  );
  return Boolean(cleanPart(c.address) && !hasStructured);
}

/** เติมฟอร์มจาก DTO — ถ้ายังไม่แยก จะลอง parse / ใส่ addressLine ชั่วคราว */
export function toCustomerAddressFormValue(c: {
  village?: string | null;
  district?: string | null;
  province?: string | null;
  addressLine?: string | null;
  address?: string | null;
}): CustomerAddressFormValue {
  if (c.village || c.district || c.province || c.addressLine) {
    return {
      province: c.province ?? "",
      district: c.district ?? "",
      village: c.village ?? "",
      addressLine: c.addressLine ?? "",
    };
  }

  const parsed = parseCustomerAddressForBackfill(c.address);
  if (parsed.parsed) {
    return {
      province: parsed.province ?? "",
      district: parsed.district ?? "",
      village: parsed.village ?? "",
      addressLine: parsed.addressLine ?? "",
    };
  }

  // parse ไม่ได้ — คงข้อความไว้ใน addressLine กันข้อมูลหาย + ให้ UI แสดง hint
  return {
    ...EMPTY_CUSTOMER_ADDRESS_FORM,
    addressLine: c.address ?? "",
  };
}

export function customerAddressFormToPayload(value: CustomerAddressFormValue) {
  return {
    province: value.province.trim() || null,
    district: value.district.trim() || null,
    village: value.village.trim() || null,
    addressLine: value.addressLine.trim() || null,
  };
}

/**
 * ตัดสินใจค่าที่จะเขียนตอน upsert
 * - มี structured keys ใน input → เขียนฟิลด์แยก + regenerate address
 * - มีแค่ address (legacy) → เขียน address; บน update ไม่แตะฟิลด์แยก
 */
export function resolveCustomerAddressForWrite(
  input: StructuredCustomerAddress & { address?: string | null },
  options?: { isUpdate?: boolean },
): ResolvedCustomerAddressWrite {
  const isUpdate = options?.isUpdate ?? false;
  const structuredProvided =
    input.village !== undefined ||
    input.district !== undefined ||
    input.province !== undefined ||
    input.addressLine !== undefined;

  const village = cleanPart(input.village);
  const district = cleanPart(input.district);
  const province = cleanPart(input.province);
  const addressLine = cleanPart(input.addressLine);
  const hasStructuredValue = Boolean(
    village || district || province || addressLine,
  );

  if (structuredProvided || hasStructuredValue) {
    const formatted = formatCustomerAddress({
      village,
      district,
      province,
      addressLine,
    });
    return {
      touchStructured: true,
      village,
      district,
      province,
      addressLine,
      address: formatted ?? cleanPart(input.address),
    };
  }

  return {
    touchStructured: !isUpdate,
    village: null,
    district: null,
    province: null,
    addressLine: null,
    address: cleanPart(input.address),
  };
}

type ClassifiedPart =
  | { kind: "village"; value: string }
  | { kind: "district"; value: string }
  | { kind: "province"; value: string }
  | { kind: "unknown"; value: string };

function classifyPart(raw: string): ClassifiedPart {
  const part = raw.trim();
  if (!part) return { kind: "unknown", value: part };

  const villageMatch = part.match(/^(?:ບ\s+|ບ້ານ\s*)(.+)$/u);
  if (villageMatch?.[1]?.trim()) {
    return { kind: "village", value: villageMatch[1].trim() };
  }

  const districtMatch = part.match(/^(?:ມ\s+|ເມືອງ\s*)(.+)$/u);
  if (districtMatch?.[1]?.trim()) {
    return { kind: "district", value: districtMatch[1].trim() };
  }

  const provinceMatch = part.match(/^(?:ຂ\s+|ແຂວງ\s*)(.+)$/u);
  if (provinceMatch?.[1]?.trim()) {
    return { kind: "province", value: provinceMatch[1].trim() };
  }

  return { kind: "unknown", value: part };
}

/**
 * แยกที่อยู่ข้อความก้อนเดียวเป็นฟิลด์ — best-effort สำหรับ backfill
 */
export function parseCustomerAddressForBackfill(
  raw: string | null | undefined,
): ParsedCustomerAddress {
  const empty: ParsedCustomerAddress = {
    village: null,
    district: null,
    province: null,
    addressLine: null,
    parsed: false,
  };

  const text = raw?.trim() ?? "";
  if (!text) return empty;

  const parts = text
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return empty;

  const classified = parts.map(classifyPart);
  const hasPrefix = classified.some((c) => c.kind !== "unknown");

  let village: string | null = null;
  let district: string | null = null;
  let province: string | null = null;
  let addressLine: string | null = null;

  if (hasPrefix) {
    const unknowns: string[] = [];
    for (const c of classified) {
      if (c.kind === "village" && !village) village = c.value;
      else if (c.kind === "district" && !district) district = c.value;
      else if (c.kind === "province" && !province) province = c.value;
      else if (c.kind === "unknown") unknowns.push(c.value);
      else unknowns.push(c.value);
    }
    if (unknowns.length === 1 && !province) {
      province = unknowns[0] ?? null;
    } else if (unknowns.length > 0) {
      addressLine = unknowns.join(", ");
    }
  } else if (parts.length === 3) {
    village = parts[0] ?? null;
    district = parts[1] ?? null;
    province = parts[2] ?? null;
  } else if (parts.length === 2) {
    village = parts[0] ?? null;
    province = parts[1] ?? null;
  } else if (parts.length === 1) {
    return empty;
  } else {
    const head = parts.slice(0, -3);
    const tail = parts.slice(-3);
    addressLine = head.join(", ") || null;
    village = tail[0] ?? null;
    district = tail[1] ?? null;
    province = tail[2] ?? null;
  }

  const parsed = Boolean(village || district || province || addressLine);
  if (!parsed) return empty;

  return { village, district, province, addressLine, parsed };
}
